import { PaymentMethod } from '@/types';
import {
  DOLLAR_PAYMENT_METHOD,
  TRANSLATED_PAYMENT_METHOD,
} from '@/constants/defaultPaymentMethodContant';

export const getPaymentMethodSpanishName = (paymentMethod: PaymentMethod): string =>
  TRANSLATED_PAYMENT_METHOD[Number(paymentMethod.defaultPaymentMethodId)] ?? paymentMethod.name;

export const isDollarPaymentMethod = (paymentMethod: PaymentMethod): boolean => {
  if (!paymentMethod) return false;
  return paymentMethod.defaultPaymentMethodId === DOLLAR_PAYMENT_METHOD;
};
export const convertFromDollars = (amount: number, exchangeRate: number): number => {
  if (!exchangeRate || exchangeRate <= 0) {
    return amount;
  }
  return amount * exchangeRate;
};

export const convertToDollars = (amount: number, exchangeRate: number): number => {
  if (!exchangeRate || exchangeRate <= 0) {
    return amount;
  }
  return amount / exchangeRate;
};

export const calculateTotalSelectedInColones= (
  selectedMethods: { methodId: string; amount: number }[],
  paymentMethods: PaymentMethod[],
  exchangeRate: number
): number => {
  return selectedMethods.reduce((sum, method) => {
    const paymentMethod = paymentMethods.find(pm => pm.id === method.methodId);
    const isDollar = paymentMethod && isDollarPaymentMethod(paymentMethod);
    const convertedAmount = isDollar ? convertFromDollars(method.amount, exchangeRate) : method.amount;
    
    return sum + convertedAmount;
  }, 0);
};

export const isTransactionComplete = (
  selectedMethods: { methodId: string; amount: number }[],
  paymentMethods: PaymentMethod[],
  totalAmount: number,
  exchangeRate: number
): boolean => {
  if (!totalAmount || !selectedMethods || selectedMethods.length === 0) return false;
  
  const totalSelected = calculateTotalSelectedInColones(selectedMethods, paymentMethods, exchangeRate);
  return totalSelected >= totalAmount;
};

export const formatAmountForDisplay = (amount: number, isDollar: boolean = false): number => {
  if (isDollar) {
    return Math.round(amount * 100) / 100;
  }
  return amount;
};

const TOLERANCE_AMOUNT = 0.01; 

// Check if the remaining amount is within tolerance and should be considered as zero
export const isWithinTolerance = (remainingAmount: number): boolean => {
  return Math.abs(remainingAmount) <= TOLERANCE_AMOUNT;
};

// Adjust amount to handle small decimal differences
export const adjustAmountForTolerance = (amount: number): number => {
  if (isWithinTolerance(amount)) {
    return 0;
  }
  return amount;
};

export const isTransactionCompleteWithTolerance = (
  selectedMethods: { methodId: string; amount: number }[],
  paymentMethods: PaymentMethod[],
  totalAmount: number,
  exchangeRate: number
): boolean => {
  if (!totalAmount || !selectedMethods || selectedMethods.length === 0) return false;
  
  const totalSelected = calculateTotalSelectedInColones(selectedMethods, paymentMethods, exchangeRate);
  const remaining = totalAmount - totalSelected;
  
  return remaining <= TOLERANCE_AMOUNT;
};

export const calculateTotalDollarsReceived = (
  paymentMethods: { methodId: string; amount: number }[],
  availablePaymentMethods: PaymentMethod[]
): number => {
  if (!paymentMethods || paymentMethods.length === 0) return 0;
  
  return paymentMethods.reduce((sum, payment) => {
    const paymentMethod = availablePaymentMethods.find(pm => pm.id === payment.methodId);
    const isDollarPayment = paymentMethod?.defaultPaymentMethodId === DOLLAR_PAYMENT_METHOD;
    
    if (isDollarPayment) {
      return sum + payment.amount; // Keep original dollar amount
    }
    return sum; 
  }, 0);
};

export const adjustPaymentAmountsForSale = (
  paymentMethods: { methodId: string; amount: number; defaultPaymentMethodId?: number }[],
  totalAmount: number,
  availablePaymentMethods: PaymentMethod[],
  exchangeRate: number
): { methodId: string; amount: number; defaultPaymentMethodId?: number }[] => {
  if (!paymentMethods || paymentMethods.length === 0) return paymentMethods;
  
  // Calculate total amount being paid in colones
  const totalBeingPaid = paymentMethods.reduce((sum, payment) => {
    const paymentMethod = availablePaymentMethods.find(pm => pm.id === payment.methodId);
    const isDollarPayment = paymentMethod?.defaultPaymentMethodId === DOLLAR_PAYMENT_METHOD;
    
    if (isDollarPayment) {
      return sum + convertFromDollars(payment.amount, exchangeRate);
    }
    return sum + payment.amount;
  }, 0);
  
  // adjust not needed
  if (totalBeingPaid <= totalAmount) {
    return paymentMethods;
  }
  
  //distribute the actual total proportionally among ALL payment methods
  let remainingTotal = totalAmount;
  const adjustedPayments: { methodId: string; amount: number; defaultPaymentMethodId?: number }[] = [];
  
  //identify all payments and calculate their amounts in colones
  const allPayments = paymentMethods.map(payment => {
    const paymentMethod = availablePaymentMethods.find(pm => pm.id === payment.methodId);
    const isDollarPayment = paymentMethod?.defaultPaymentMethodId === DOLLAR_PAYMENT_METHOD;
    
    const amountInColones = isDollarPayment ? convertFromDollars(payment.amount, exchangeRate) : payment.amount;
    return { ...payment, amountInColones, isDollarPayment };
  });
  
  //distribute the total proportionally
  for (let i = 0; i < allPayments.length; i++) {
    const payment = allPayments[i];
    const isLastPayment = i === allPayments.length - 1;
    
    //use all remaining total
    const adjustedAmountInColones = isLastPayment ? remainingTotal : Math.min(payment.amountInColones, remainingTotal);
    
    //convert back to original currency if needed
    const adjustedAmount = payment.isDollarPayment ? 
      convertToDollars(adjustedAmountInColones, exchangeRate) : 
      adjustedAmountInColones;
    
    adjustedPayments.push({
      methodId: payment.methodId,
      amount: adjustedAmount,
      defaultPaymentMethodId: payment.defaultPaymentMethodId
    });
    
    remainingTotal -= adjustedAmountInColones;
  }
  
  return adjustedPayments;
};